import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown } from "lucide-react";
import { getCachedTiers, fetchTiers, subscribeTiers } from "@/lib/tierConfig";
import { getCachedSettings, fetchSettings, subscribeSettings } from "@/lib/settingsConfig";

// Visuelle Tier-Stile (Farben/Akzente) – min_ovr & background_url werden zur Laufzeit per API überschrieben.
const TIER_STYLES = {
  bronze: {
    base: "#2A1610", accent: "#CD7F32", light: "#E0995C",
    ovr: "#F0B97A", panelOvr: "#F0B97A",
  },
  silver: {
    base: "#1A1A20", accent: "#C0C0C0", light: "#E8E8E8",
    ovr: "#F0F0F0", panelOvr: "#F0F0F0",
  },
  gold: {
    base: "#2A2210", accent: "#D4AF37", light: "#F3CD5D",
    ovr: "#FFD86B", panelOvr: "#FFD86B",
  },
  whitegold: {
    base: "#1F1D18", accent: "#B8860B", light: "#FFFDF0",
    ovr: "#8B6914", panelOvr: "#FFE9A8",
  },
  rookie: {
    base: "#15171C", accent: "#64748B", light: "#94A3B8",
    ovr: "#CBD5E1", panelOvr: "#E2E8F0",
  },
  champion: {
    base: "#1B1230", accent: "#A855F7", light: "#E9D5FF",
    ovr: "#F3D9FF", panelOvr: "#F3D9FF",
  },
};

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
};
const lighten = (hex, amt = 0.45) => {
  const rgb = hexToRgb(hex); if (!rgb) return hex;
  const out = rgb.map((v) => Math.round(v + (255 - v) * amt));
  return `#${out.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

const buildTier = (key, tiersFromConfig) => {
  const cfg = tiersFromConfig.find((t) => t.key === key) || { label: key, background_url: "", accent_color: "" };
  const style = TIER_STYLES[key] || TIER_STYLES.silver;
  const t = { key, name: cfg.label, bg: cfg.background_url || "", ...style };
  if (cfg.accent_color) {
    t.accent = cfg.accent_color;
    t.light = lighten(cfg.accent_color, 0.55);
    t.ovr = lighten(cfg.accent_color, 0.65);
    t.panelOvr = lighten(cfg.accent_color, 0.65);
  }
  return t;
};

const pickTier = (ovr, tiersFromConfig) => {
  // Wähle das höchste Tier, dessen min_ovr <= ovr ist
  const ranked = tiersFromConfig
    .filter((t) => t.key !== "rookie")
    .slice()
    .sort((a, b) => (b.min_ovr || 0) - (a.min_ovr || 0));
  const match = ranked.find((t) => (ovr || 0) >= (t.min_ovr || 0));
  return buildTier(match ? match.key : "bronze", tiersFromConfig);
};

export default function DriverCard({ driver, team, teamsById = {}, onClick, testId, staticFront = false, hidden = false }) {
  const [flipped, setFlipped] = useState(false);
  const [tiersCfg, setTiersCfg] = useState(getCachedTiers());
  const [settings, setSettings] = useState(getCachedSettings());

  useEffect(() => {
    fetchTiers();
    fetchSettings();
    const unsub = subscribeTiers(setTiersCfg);
    const unsub2 = subscribeSettings(setSettings);
    return () => { unsub(); unsub2(); };
  }, []);

  const deltaBigThreshold = settings.delta_big_threshold || 5;
  const prev = driver.previous_stats || null;
  const prevStats = prev?.stats || {};
  const prevOvr = prev?.ovr;
  const ovrDelta = (typeof prevOvr === "number" && typeof driver.ovr === "number") ? (driver.ovr - prevOvr) : 0;

  const teamColor = team?.color || "#00E5FF";
  const isRookieDriver = !!driver.is_rookie;
  const isChampion = !!driver.is_champion && !isRookieDriver;
  const tier = isRookieDriver
    ? buildTier("rookie", tiersCfg)
    : isChampion
    ? buildTier("champion", tiersCfg)
    : pickTier(driver.ovr, tiersCfg);
  const handleFlip = () => {
    setFlipped((f) => !f);
    onClick && onClick(driver);
  };

  const stats = driver.stats || {};
  const isRookie = !!driver.is_rookie;
  const placeholder = "??";
  const ovrDisplay = isRookie ? placeholder : driver.ovr;
  const statRows = [
    ["PAC", isRookie ? placeholder : stats.pac],
    ["KON", isRookie ? placeholder : stats.kon],
    ["RAC", isRookie ? placeholder : stats.rac],
    ["CLE", isRookie ? placeholder : stats.cle],
    ["QUA", isRookie ? placeholder : stats.qua],
    ["ERG", isRookie ? placeholder : stats.erg],
    ["ZUV", isRookie ? placeholder : stats.zuv],
  ];

  const pastTeams = (driver.past_team_ids || []).map(id => teamsById[id]).filter(Boolean).slice().reverse();

  // Verdeckte Karte (z. B. Pre-Launch Reveal-Phase): einheitliches Card-Back-Design
  if (hidden) {
    return (
      <div className="perspective-1000 w-72 h-[420px] mx-auto" data-testid={testId || "driver-card-hidden"}>
        <div
          className="relative w-full h-full chamfer overflow-hidden flex items-center justify-center"
          style={{
            background: "radial-gradient(ellipse at center, #1a1a1a 0%, #050505 100%)",
            border: "2px solid rgba(255,255,255,0.08)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.7), inset 0 0 60px rgba(0,229,255,0.06)",
          }}
        >
          {/* Diagonale Streifen */}
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage: "repeating-linear-gradient(45deg, #fff 0 2px, transparent 2px 14px)",
            }}
          />
          {/* Großes Fragezeichen */}
          <div className="relative text-center">
            <div
              className="font-display font-black italic text-[180px] leading-none"
              style={{ color: "rgba(255,255,255,0.08)", letterSpacing: "-0.08em" }}
            >
              ?
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="font-display font-black italic uppercase tracking-widest text-base"
                style={{ color: "#00E5FF", textShadow: "0 0 18px rgba(0,229,255,0.6)" }}
              >
                Verdeckt
              </div>
            </div>
          </div>
          {/* Sector Four Brand Foot */}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <div className="font-display font-black italic uppercase text-[10px] tracking-[0.3em] text-neutral-500">
              Sector Four
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="perspective-1000 w-72 h-[420px] mx-auto" data-testid={testId || "driver-card"}>
      <motion.div
        className="relative w-full h-full preserve-3d cursor-pointer"
        animate={staticFront ? { rotateY: 0 } : { rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        onClick={staticFront ? undefined : handleFlip}
        style={staticFront ? { transformStyle: "flat", transform: "none" } : undefined}
      >
        {/* FRONT */}
        <div className="absolute inset-0 backface-hidden chamfer overflow-hidden"
          style={{
            background: tier.bg
              ? "#000"
              : `linear-gradient(160deg, ${tier.accent}33 0%, ${tier.base} 38%, #0B0F19 100%)`,
          }}
        >
          {tier.bg && (
            <img
              src={tier.bg}
              alt=""
              className="absolute inset-0 w-full h-full object-cover z-[1] pointer-events-none select-none"
              draggable={false}
            />
          )}
          <div className="absolute inset-0 diag-stripes z-[3]" style={{ ["--stripe-color"]: `${tier.accent}1F` }} />
          <div className="absolute top-0 left-0 right-0 h-1.5 z-[4]" style={{ background: `linear-gradient(90deg, transparent, ${tier.light}, ${tier.accent}, ${tier.light}, transparent)` }} />

          {/* NEW badge for rookies */}
          {isRookie && (
            <div
              className="absolute top-3 right-3 z-30 font-display font-black italic uppercase tracking-widest text-[10px] px-2 py-1"
              style={{
                background: "#00E5FF",
                color: "#000",
                clipPath: "polygon(10% 0, 100% 0, 90% 100%, 0 100%)",
                boxShadow: "0 4px 14px rgba(0,229,255,0.45)",
                letterSpacing: "0.18em",
              }}
              data-testid="rookie-new-badge"
            >
              New
            </div>
          )}

          {/* Champion crown badge */}
          {isChampion && (
            <div
              className="absolute top-3 left-3 z-30 flex items-center gap-1.5 px-2.5 py-1 font-display font-black italic uppercase tracking-widest text-[10px]"
              style={{
                background: `linear-gradient(135deg, ${tier.accent}, ${tier.light})`,
                color: "#000",
                clipPath: "polygon(0 0, 92% 0, 100% 100%, 8% 100%)",
                boxShadow: `0 4px 16px ${tier.accent}80`,
                letterSpacing: "0.18em",
              }}
              data-testid="champion-badge"
            >
              <Crown className="w-3 h-3" strokeWidth={2.5} />
              <span>{tier.name || "Champion"}{driver.champion_season ? ` ${driver.champion_season}` : ""}</span>
            </div>
          )}

          {/* Top-left rating block */}
          <div
            className="absolute top-4 left-4 z-20 flex flex-col items-center px-3 py-2.5 backdrop-blur-md border"
            style={{
              background: `linear-gradient(180deg, ${tier.light}1F 0%, ${tier.base}99 100%)`,
              borderColor: `${tier.accent}33`,
              boxShadow: `inset 0 1px 0 ${tier.light}33, 0 4px 18px rgba(0,0,0,0.45)`,
              clipPath: "polygon(8% 0, 100% 0, 100% 92%, 92% 100%, 0 100%, 0 8%)",
            }}
          >
            <div className="relative inline-flex items-start">
              <div className="font-display font-black italic leading-none" style={{ fontSize: "3.25rem", color: tier.ovr, textShadow: `0 2px 12px ${tier.accent}66` }}>
                {ovrDisplay}
              </div>
              {!isRookie && ovrDelta !== 0 && (
                <div
                  className="font-display font-black text-[10px] leading-none ml-1 mt-3 px-1 py-0.5"
                  style={{
                    color: ovrDelta > 0 ? "#22c55e" : "#ef4444",
                    background: ovrDelta > 0 ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                    border: `1px solid ${ovrDelta > 0 ? "#22c55e66" : "#ef444466"}`,
                  }}
                  data-testid="ovr-delta"
                  title={`Veränderung zur letzten Karte (${prev?.season_name || "Vorgänger"})`}
                >
                  {ovrDelta > 0 ? "+" : ""}{ovrDelta}
                </div>
              )}
            </div>
            <div className="font-display font-bold uppercase tracking-widest text-white/85 text-xs mt-0.5">
              #{driver.number || "-"}
            </div>
            <div className="mt-2 relative" title={driver.country || driver.country_code}>
              <img
                src={`https://flagcdn.com/w40/${(driver.country_code || "de").toLowerCase()}.png`}
                srcSet={`https://flagcdn.com/w80/${(driver.country_code || "de").toLowerCase()}.png 2x`}
                alt={driver.country_code || ""}
                width={28}
                height={20}
                className="object-cover border border-white/15"
                style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" }}
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "inline-block"; }}
                draggable={false}
              />
              <span className="hidden px-2 py-0.5 text-[10px] font-bold tracking-widest border border-white/20 text-white/80">
                {driver.country_code || "—"}
              </span>
            </div>
            {(() => {
              const isInactive = driver.is_active === false;
              if (isInactive) {
                const inactiveLogo = settings.inactive_logo_url;
                if (inactiveLogo) {
                  return (
                    <img
                      src={inactiveLogo}
                      alt="Inaktiv"
                      className="mt-3 w-20 h-20 object-contain opacity-90"
                      style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.7))" }}
                      draggable={false}
                      data-testid="driver-card-inactive-logo"
                    />
                  );
                }
                return (
                  <div
                    className="mt-3 w-20 h-20 flex items-center justify-center font-display font-black italic uppercase text-[11px] leading-tight text-center px-1"
                    style={{
                      background: "#1a1a1a",
                      color: "#9ca3af",
                      border: "1px solid #374151",
                      clipPath: "polygon(15% 0, 100% 0, 100% 85%, 85% 100%, 0 100%, 0 15%)",
                    }}
                    title="Inaktiver Fahrer"
                    data-testid="driver-card-inactive-logo"
                  >
                    Inaktiv
                  </div>
                );
              }
              if (!team) return null;
              return team.badge_url ? (
                <img
                  src={team.badge_url}
                  alt={team.name}
                  className="mt-3 w-20 h-20 object-contain"
                  style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.7))" }}
                  draggable={false}
                />
              ) : (
                <div
                  className="mt-3 w-20 h-20 flex items-center justify-center font-display font-black italic uppercase text-base leading-none"
                  style={{ background: teamColor, color: "#000", clipPath: "polygon(15% 0, 100% 0, 100% 85%, 85% 100%, 0 100%, 0 15%)" }}
                  title={team.name}
                >
                  {team.short_name}
                </div>
              );
            })()}
          </div>

          {/* Portrait or placeholder */}
          {driver.photo_url ? (
            <img
              src={driver.photo_url}
              alt={driver.name}
              className="absolute right-0 bottom-[26%] w-[80%] max-h-[68%] object-contain object-bottom z-[8] pointer-events-none transition-transform duration-500 group-hover:scale-105"
              style={{ filter: "drop-shadow(0 18px 24px rgba(0,0,0,0.6))", transformOrigin: "bottom right" }}
              draggable={false}
            />
          ) : (
            <div
              className="absolute right-[8%] bottom-[28%] w-[48%] aspect-square z-[8] flex items-center justify-center font-display font-black italic uppercase text-white/60"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", fontSize: "3.5rem", clipPath: "polygon(8% 0, 100% 0, 100% 92%, 92% 100%, 0 100%, 0 8%)" }}
            >
              {initialsOf(driver.name)}
            </div>
          )}

          {/* Bottom stats panel */}
          <div
            className="absolute bottom-0 left-0 right-0 z-20 p-4 backdrop-blur-md border-t"
            style={{
              background: `linear-gradient(180deg, ${tier.accent}26 0%, ${tier.base}E6 45%, #0B0F19F2 100%)`,
              borderTopColor: `${tier.accent}80`,
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${tier.light}AA, transparent)` }} />
            <div className="font-display font-black italic uppercase tracking-tight text-xl leading-none truncate text-center" style={{ color: tier.panelOvr, textShadow: `0 1px 6px ${tier.accent}40` }}>
              {driver.name}
            </div>
            <div className="grid grid-cols-4 gap-x-2 gap-y-1.5 mt-3">
              {statRows.map(([k, v]) => {
                const lowK = k.toLowerCase();
                const prevVal = prevStats[lowK];
                const delta = (!isRookie && typeof prevVal === "number" && typeof v === "number") ? (v - prevVal) : 0;
                let Arrow = null;
                let arrowColor = null;
                if (delta !== 0) {
                  const big = Math.abs(delta) >= deltaBigThreshold;
                  if (delta > 0) { Arrow = big ? ChevronsUp : ChevronUp; arrowColor = "#22c55e"; }
                  else { Arrow = big ? ChevronsDown : ChevronDown; arrowColor = "#ef4444"; }
                }
                return (
                  <div key={k} className="flex items-baseline gap-1">
                    <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: `${tier.light}99` }}>{k}</span>
                    <span className="font-display font-black text-sm" style={{ color: tier.panelOvr }}>{v ?? "-"}</span>
                    {Arrow && (
                      <Arrow
                        className="w-3 h-3 -ml-0.5 shrink-0"
                        style={{ color: arrowColor }}
                        strokeWidth={3}
                        aria-label={`${delta > 0 ? "+" : ""}${delta}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* BACK */}
        {!staticFront && (
        <div className="absolute inset-0 backface-hidden rotate-y-180 chamfer overflow-hidden bg-[#141A26] border border-white/10 p-5 flex flex-col">
          <div className="eyebrow">Karriere</div>
          <div className="font-display font-black italic uppercase text-2xl leading-tight mt-1">{driver.name}</div>
          <div className="text-sm text-neutral-400 mt-1">{team?.name || "Privat"} · #{driver.number || "-"}</div>

          {/* Market value */}
          <div className="mt-3 flex items-center justify-between border border-white/10 px-3 py-2 bg-[#0B0F19]">
            <span className="text-[10px] uppercase tracking-widest text-neutral-400">Marktwert</span>
            <span className="font-display font-black italic text-xl leading-none" style={{ color: tier.panelOvr }}>
              {formatEuro(driver.market_value)}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
            <Stat label="Rennen" value={driver.races || driver.career_auto?.races || 0} />
            <Stat label="Podien" value={driver.podiums || driver.career_auto?.podiums || 0} />
            <Stat label="Siege" value={driver.wins || driver.career_auto?.wins || 0} />
            <Stat label="Titel" value={driver.titles || driver.career_auto?.titles || 0} highlight={(driver.titles || driver.career_auto?.titles || 0) > 0} />
            <Stat label="Punkte" value={driver.points || driver.career_auto?.points || 0} />
            <Stat label="Poles" value={driver.poles || driver.career_auto?.poles || 0} />
            <Stat label="DNFs" value={driver.dnfs || driver.career_auto?.dnfs || 0} />
            <Stat label="PP" value={driver.penalty_points} danger={driver.penalty_points > 0} />
          </div>

          <div className="mt-4 flex-1 overflow-hidden">
            <div className="eyebrow text-[10px]">Vergangene Teams</div>
            {pastTeams.length === 0 ? (
              <div className="text-xs text-neutral-500 mt-2">Keine vorherigen Teams hinterlegt.</div>
            ) : (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {pastTeams.map((pt, idx) => (
                  <div key={pt.id} className="flex items-center gap-2">
                    <div
                      className="w-9 h-9 flex items-center justify-center border border-white/10 bg-[#0B0F19]"
                      title={pt.name}
                    >
                      {pt.badge_url ? (
                        <img src={pt.badge_url} alt={pt.name} className="w-7 h-7 object-contain" />
                      ) : (
                        <div className="w-7 h-7 flex items-center justify-center font-display font-black italic text-[10px]" style={{ background: pt.color, color: "#000" }}>
                          {pt.short_name}
                        </div>
                      )}
                    </div>
                    {idx < pastTeams.length - 1 && (
                      <svg width="10" height="10" viewBox="0 0 10 10" className="text-neutral-500" aria-hidden="true">
                        <path d="M2 5h6M5.5 2.5L8 5l-2.5 2.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-2 text-[10px] uppercase tracking-widest text-neutral-500">Klicken zum Zurückdrehen</div>
        </div>
        )}
      </motion.div>
    </div>
  );
}

const Stat = ({ label, value, highlight, danger }) => (
  <div className={`border px-2 py-1.5 flex flex-col items-center justify-center text-center ${highlight ? "border-[#00E5FF]/50 bg-[#00E5FF]/10" : danger ? "border-[#FF3B30]/40 bg-[#FF3B30]/10" : "border-white/10"}`}>
    <div className="text-[9px] uppercase tracking-widest text-neutral-300">{label}</div>
    <div className={`font-display font-black text-lg leading-none mt-0.5 ${highlight ? "text-[#00E5FF]" : danger ? "text-[#FF3B30]" : "text-white"}`}>{value ?? 0}</div>
  </div>
);

const initialsOf = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("") || "??";

const formatEuro = (v) => {
  const n = Number(v) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 2 })} Mio €`;
  if (n >= 1_000) return `${(n / 1_000).toLocaleString("de-DE", { maximumFractionDigits: 0 })} K €`;
  return `${n.toLocaleString("de-DE")} €`;
};
